import React, {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Autocomplete,
  TextField,
  Popper,
  autocompleteClasses,
} from "@mui/material";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";
import OAuthService from "src/app/auth/services/oAuthService";
import moment from "moment";
import CustomDatePicker from "src/app/main/utilities/CustomDatePicker";
import { styled } from "@mui/styles";
import { useTheme } from "@mui/material/styles";

const StyledPopper = styled(Popper)(({ theme }) => ({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: "border-box",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontSize: "11px",
  },
}));
export const UniqueIdEditor = forwardRef((props, ref) => {
  const { value: initialValue, node, onLiveChange } = props;
  const [value, setValue] = useState(initialValue ?? "");
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useImperativeHandle(ref, () => ({
    getValue() {
      return value;
    },
    isCancelBeforeStart() {
      return false;
    },
    isCancelAfterEnd() {
      return false;
    },
  }));

  const handleChange = (e) => {
    const newVal = e.target.value.toUpperCase();
    setValue(newVal);
    onLiveChange?.(node, newVal);
  };

  return (
    <input
      ref={inputRef}
      className="ag-cell-edit-input"
      style={{ width: "100%", height: "100%",textTransform: "uppercase", border: "none", outline: "none" }}
      value={value}
      onChange={handleChange}
    />
  );
});
export const SelectProduct = React.memo(
  forwardRef((props, ref) => {
    const refVendor = useRef();
    const [vendorOptions, setVendorOptions] = useState([]);
    const [vendorShortKey, setVendorShortKey] = useState(props?.value);
    const [vendorNumber, setVendorNumber] = useState(
      props?.data?.vendorNum || "",
    );
    useEffect(() => {
      setVendorShortKey(props?.value);
      setVendorNumber(props?.data?.vendorNum || "");
    }, [props?.value, props?.data]);

    const getVendorsList = async () => {
      const url = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.factoryVendor}`;
      try {
        const list = await OAuthService.getList(url + `?size=1000`);
        let factoryVendorData = list.data?._embedded?.factoryVendors;
        factoryVendorData = factoryVendorData.map((vendor) => {
          return {
            label: vendor.shortKey,
            value: vendor.id,
            name: vendor.name,
          };
        });
        setVendorOptions(factoryVendorData);
      } catch (error) {
        console.log(error.message);
      }
    };
    useEffect(() => {
      getVendorsList();
    }, []);

    const handleVendorChange = (e, data) => {
      setVendorShortKey(data.label);
      setVendorNumber(data.value);
      const rowData = props?.node;
      rowData?.setDataValue("vendorNum", data.value);
      rowData?.setDataValue("vendorName", data.name);
    };

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return vendorShortKey;
        },
        getVendorNum() {
          return vendorNumber;
        },
      };
    });

    const selectedValue =
      vendorOptions.find(
        (obj) =>
          obj.label?.trim() === vendorShortKey?.trim() ||
          obj.value === vendorNumber,
      ) ||
      (vendorShortKey
        ? {
            label: vendorShortKey,
            value: vendorNumber || vendorShortKey,
            name: props.data?.vendorName,
          }
        : null);

    const displayOptions =
      selectedValue &&
      !vendorOptions.some((o) => o.label === selectedValue.label)
        ? [...vendorOptions, selectedValue]
        : vendorOptions;

    return (
      <Autocomplete
        options={displayOptions}
        value={selectedValue}
        isOptionEqualToValue={(option, val) =>
          option.value === val?.value || option.label === val?.label
        }
        onChange={handleVendorChange}
        disableClearable
        PopperComponent={StyledPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            className="customFieldOMS"
            placeholder={"Select "}
            inputRef={refVendor}
          />
        )}
      />
    );
  }),
);

export const SelectRange1 = React.memo(
  forwardRef((props, ref) => {
    const url = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.containerByShippingType}`;
    const refRange = useRef();
    const [rangeOptions, setRangeOptions] = useState([]);
    const [vendorNumber, setVendorNumber] = useState(props.data?.vendorNum);
    const [range, setRange] = useState(props?.value);
    const [rangeId, setRangeId] = useState(props?.data?.range1Id);
    const theme = useTheme();

    useEffect(() => {
      setRange(props?.value);
      setRangeId(props?.data?.range1Id);
      setVendorNumber(props.data?.vendorNum);
    }, [props?.value, props?.data]);

    const getRangesByVendor = async () => {
      if (vendorNumber) {
        const rangeByVenderUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findRangeByVendor}`;
        const list = await OAuthService.getList(
          `${rangeByVenderUrl}?vendorId=${vendorNumber}`,
        );
        let ranges = list.data?._embedded?.ranges;
        ranges = ranges.map((range) => ({
          id: range.id,
          value: range.productId,
          label: range.productName,
        }));
        setRangeOptions(ranges);
      }
    };

    const handleRangeChange = (e, data) => {
      setRange(data.label);
      setRangeId(data.id);
      const rowData = props?.node;
      rowData?.setDataValue("range1Id", data.value);
      props.api
        ?.getCellEditorInstances({ rowNodes: [props.node] })
        .forEach((editor) => {
          if (editor?.refreshForNewRange1) {
            editor.refreshForNewRange1(data.value);
          }
          if (editor?.updateCarcassRangeId1) {
            editor.updateCarcassRangeId1(data.value);
          }
          if (editor?.updateHandleRangeId1) {
            editor.updateHandleRangeId1(data.value);
          }
        });
    };

    const getPriceGroup = async () => {
      const rangeUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.ranges}`;
      const list = await OAuthService.getList(
        `${rangeUrl}/${rangeId}/priceGroup`,
      );
      const rowData = props?.node;
      const newPgId = list.data?.pgId;
      const currentPgId = props.data?.priceGroup1;

      if (newPgId && String(newPgId) !== String(currentPgId)) {
        rowData?.setDataValue("priceGroup1", newPgId);
      }
    };
    useEffect(() => {
      if (vendorNumber) {
        getRangesByVendor();
      }
      if (rangeId) {
        getPriceGroup();
      }
    }, [vendorNumber, rangeId]);

    const handleFocus = () => {
      props.api?.getCellEditorInstances().map((editor) => {
        if (editor?.getVendorNum) {
          setVendorNumber(editor.getVendorNum());
        }
      });
    };

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return range;
        },
        getRangeId() {
          return rangeId;
        },
      };
    });

    const selectedValue =
      rangeOptions.find(
        (obj) =>
          obj.value === rangeId ||
          obj.label?.trim() === range?.trim() ||
          range?.includes(obj.label?.trim()),
      ) || (range ? { label: range, value: rangeId || range } : null);

    const displayOptions =
      selectedValue &&
      !rangeOptions.some((o) => o.label === selectedValue.label)
        ? [...rangeOptions, selectedValue]
        : rangeOptions;

    return (
      <Autocomplete
        options={displayOptions}
        value={selectedValue}
        isOptionEqualToValue={(option, val) =>
          option.value === val?.value || option.label === val?.label
        }
        onChange={handleRangeChange}
        onFocus={handleFocus}
        disableClearable
        sx={{
          "& .MuiInputBase-input": {
            color: theme.palette.text.primary,
          },
        }}
        PopperComponent={StyledPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            className="customFieldOMS"
            placeholder={"Select "}
            inputRef={refRange}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input::placeholder": {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },

              "& .MuiSvgIcon-root": {
                color: theme.palette.text.primary,
              },
            }}
          />
        )}
      />
    );
  }),
);

export const SelectRange2 = React.memo(
  forwardRef((props, ref) => {
    const refRange = useRef();
    const [rangeOptions, setRangeOptions] = useState([]);
    const [vendorNumber, setVendorNumber] = useState(props.data?.vendorNum);
    const [range, setRange] = useState(props?.value);
    const [rangeId, setRangeId] = useState(props?.data?.range2Id || "");
    const theme = useTheme();

    useEffect(() => {
      setRange(props?.value);
      setRangeId(props?.data?.range2Id || "");
      setVendorNumber(props.data?.vendorNum);
    }, [props?.value, props?.data]);

    const getRangesByVendor = async () => {
      if (vendorNumber) {
        const rangeByVenderUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findRangeByVendor}`;
        const list = await OAuthService.getList(
          `${rangeByVenderUrl}?vendorId=${vendorNumber}`,
        );
        let ranges = list.data?._embedded?.ranges;
        ranges = ranges.map((range) => ({
          id: range.id,
          value: range.productId,
          label: range.productName,
        }));
        setRangeOptions(ranges);
      }
    };

    const handleRangeChange = (e, data) => {
      setRange(data.label);
      setRangeId(data.id);
      const rowData = props?.node;
      rowData?.setDataValue("range2Id", data.value);
      props.api
        ?.getCellEditorInstances({ rowNodes: [props.node] })
        .forEach((editor) => {
          if (editor?.refreshForNewRange2) {
            editor.refreshForNewRange2(data.value);
          }
          if (editor?.updateCarcassRangeId2)
            editor.updateCarcassRangeId2(data.value);
          if (editor?.updateHandleRangeId2)
            editor.updateHandleRangeId2(data.value);
        });
    };

    const getPriceGroup = async () => {
      const rangeUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.ranges}`;
      const list = await OAuthService.getList(
        `${rangeUrl}/${rangeId}/priceGroup`,
      );
      const rowData = props?.node;
      const newPgId = list.data?.pgId;
      const currentPgId = props.data?.priceGroup2;

      if (newPgId && String(newPgId) !== String(currentPgId)) {
        rowData?.setDataValue("priceGroup2", newPgId);
      }
    };
    useEffect(() => {
      if (vendorNumber) {
        getRangesByVendor();
      }
      if (rangeId) {
        getPriceGroup();
      }
    }, [vendorNumber, rangeId]);

    const handleFocus = () => {
      props.api?.getCellEditorInstances().map((editor) => {
        if (editor?.getVendorNum) {
          setVendorNumber(editor.getVendorNum());
        }
      });
    };

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return range;
        },
        getRange2Id() {
          return rangeId;
        },
      };
    });

    const selectedValue =
      rangeOptions.find(
        (obj) =>
          obj.value === rangeId ||
          obj.label?.trim() === range?.trim() ||
          range?.includes(obj.label?.trim()),
      ) || (range ? { label: range, value: rangeId || range } : null);

    const displayOptions =
      selectedValue &&
      !rangeOptions.some((o) => o.label === selectedValue.label)
        ? [...rangeOptions, selectedValue]
        : rangeOptions;

    return (
      <Autocomplete
        options={displayOptions}
        value={selectedValue}
        isOptionEqualToValue={(option, val) =>
          option.value === val?.value || option.label === val?.label
        }
        onChange={handleRangeChange}
        onFocus={handleFocus}
        disableClearable
        PopperComponent={StyledPopper}
        sx={{
          "& .MuiInputBase-input": {
            color: theme.palette.text.primary,
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            className="customFieldOMS"
            placeholder={"Select "}
            inputRef={refRange}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input::placeholder": {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },

              "& .MuiSvgIcon-root": {
                color: theme.palette.text.primary,
              },
            }}
          />
        )}
      />
    );
  }),
);

export const SelectRange3 = React.memo(
  forwardRef((props, ref) => {
    const refRange = useRef();
    const [rangeOptions, setRangeOptions] = useState([]);
    const [vendorNumber, setVendorNumber] = useState(props.data?.vendorNum);
    const [range, setRange] = useState(props?.value);
    const [rangeId, setRangeId] = useState(props?.data?.range3Id || "");
    const theme = useTheme();

    useEffect(() => {
      setRange(props?.value);
      setRangeId(props?.data?.range3Id || "");
      setVendorNumber(props.data?.vendorNum);
    }, [props?.value, props?.data]);

    const getRangesByVendor = async () => {
      if (vendorNumber) {
        const rangeByVenderUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findRangeByVendor}`;
        const list = await OAuthService.getList(
          `${rangeByVenderUrl}?vendorId=${vendorNumber}`,
        );
        let ranges = list.data?._embedded?.ranges;
        ranges = ranges.map((range) => ({
          id: range.id,
          value: range.productId,
          label: range.productName,
        }));
        setRangeOptions(ranges);
      }
    };

    const handleRangeChange = (e, data) => {
      setRange(data.label);
      setRangeId(data.id);
      const rowData = props?.node;
      rowData?.setDataValue("range3Id", data.value);
      props.api
        ?.getCellEditorInstances({ rowNodes: [props.node] })
        .forEach((editor) => {
          if (editor?.refreshForNewRange3) {
            editor.refreshForNewRange3(data.value);
          }
          if (editor?.updateCarcassRangeId3)
            editor.updateCarcassRangeId3(data.value);
          if (editor?.updateHandleRangeId3)
            editor.updateHandleRangeId3(data.value);
        });
    };

    const getPriceGroup = async () => {
      const rangeUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.ranges}`;
      const list = await OAuthService.getList(
        `${rangeUrl}/${rangeId}/priceGroup`,
      );
      const rowData = props?.node;
      const newPgId = list.data?.pgId;
      const currentPgId = props.data?.priceGroup3;

      if (newPgId && String(newPgId) !== String(currentPgId)) {
        rowData?.setDataValue("priceGroup3", newPgId);
      }
    };
    useEffect(() => {
      if (vendorNumber) {
        getRangesByVendor();
      }
      if (rangeId) {
        getPriceGroup();
      }
    }, [vendorNumber, rangeId]);

    const handleFocus = () => {
      props.api?.getCellEditorInstances().map((editor) => {
        if (editor?.getVendorNum) {
          setVendorNumber(editor.getVendorNum());
        }
      });
    };

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return range;
        },
        getRange3Id() {
          return rangeId;
        },
      };
    });

    const selectedValue =
      rangeOptions.find(
        (obj) =>
          obj.value === rangeId ||
          obj.label?.trim() === range?.trim() ||
          range?.includes(obj.label?.trim()),
      ) || (range ? { label: range, value: rangeId || range } : null);

    const displayOptions =
      selectedValue &&
      !rangeOptions.some((o) => o.label === selectedValue.label)
        ? [...rangeOptions, selectedValue]
        : rangeOptions;

    return (
      <Autocomplete
        options={displayOptions}
        value={selectedValue}
        isOptionEqualToValue={(option, val) =>
          option.value === val?.value || option.label === val?.label
        }
        onChange={handleRangeChange}
        onFocus={handleFocus}
        disableClearable
        sx={{
          "& .MuiInputBase-input": {
            color: theme.palette.text.primary,
          },
        }}
        PopperComponent={StyledPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            className="customFieldOMS"
            placeholder={"Select "}
            inputRef={refRange}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input::placeholder": {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },

              "& .MuiSvgIcon-root": {
                color: theme.palette.text.primary,
              },
            }}
          />
        )}
      />
    );
  }),
);

export const SelectFrontColor1 = React.memo(
  forwardRef((props, ref) => {
    const refFrontColor = useRef();
    const [frontColorOptions, setFrontColorOptions] = useState([]);
    const [frontColor, setFrontColor] = useState(props?.value);
    const [rangeId, setRangeId] = useState(props?.data?.range1Id);
    const prevRangeId = useRef(props?.data?.range1Id); // NEW
    const theme = useTheme();

    useEffect(() => {
      setFrontColor(props?.value);
      setRangeId(props?.data?.range1Id);
    }, [props?.value, props?.data]);

    const getFrontColoresByVendor = async () => {
      if (!rangeId) return;

      let actualRangeId = rangeId;

      if (/^[A-Z]+/.test(rangeId)) {
        const rangeInfo = await OAuthService.getList(
          `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.ranges}/search/findRangeByProductId?productId=${rangeId}`,
        );

        actualRangeId = rangeInfo.data.id;
      }

      const frontColorUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.ranges}/${actualRangeId}/variations`;

      const list = await OAuthService.getList(frontColorUrl);

      const frontColores =
        list.data?._embedded?.rangeVariations?.map((frontColor) => ({
          value: frontColor.productId,
          label: frontColor.productName,
        })) || [];

      setFrontColorOptions(frontColores);
    };

    const handleFrontColorChange = (e, data) => {
      setFrontColor(data.label);
      const rowData = props?.node;
      rowData?.setDataValue("color1Id", data.value);
    };

    useEffect(() => {
      if (rangeId) {
        getFrontColoresByVendor();
      }
    }, [rangeId]);

    useEffect(() => {
      if (prevRangeId.current !== rangeId) {
        if (prevRangeId.current !== undefined) {
          setFrontColor("");
          const rowData = props?.node;
          rowData?.setDataValue("color1Id", "");
        }
        prevRangeId.current = rangeId;
      }
    }, [rangeId]);
    useEffect(() => {
      const rowId = props?.node?.data?.id;
      const isInvalid = !!rangeId && !frontColor;
      props.onValidityChange?.(rowId, "color1", isInvalid);
    }, [rangeId, frontColor]);

    useEffect(() => {
      return () => {
        const rowId = props?.node?.data?.id;
        props.onValidityChange?.(rowId, "color1", false);
      };
    }, []);
    const handleFocus = () => {
      props.api?.getCellEditorInstances().map((editor) => {
        if (editor?.getRangeId) {
          setRangeId(editor.getRangeId());
        }
      });
    };
    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return frontColor;
        },
        refreshForNewRange1(newRangeId) {
          if (newRangeId !== rangeId) {
            setFrontColor("");
            setRangeId(newRangeId);
            const rowData = props?.node;
            rowData?.setDataValue("color1Id", "");
          }
        },
      };
    });

    const fieldId = props.colDef.field + "Id";
    const selectedValue =
      frontColorOptions.find(
        (obj) =>
          obj.value === props.data?.[fieldId] ||
          obj.label?.trim() === frontColor?.trim() ||
          frontColor?.includes(obj.label?.trim()),
      ) ||
      (frontColor
        ? { label: frontColor, value: props.data?.[fieldId] || frontColor }
        : null);

    const displayOptions = frontColorOptions;

    return (
      <Autocomplete
        options={displayOptions}
        value={selectedValue}
        isOptionEqualToValue={(option, val) =>
          option.value === val?.value || option.label === val?.label
        }
        onChange={handleFrontColorChange}
        onFocus={handleFocus}
        disableClearable
        sx={{
          "& .MuiInputBase-input": {
            color: theme.palette.text.primary,
          },
        }}
        PopperComponent={StyledPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            className="customFieldOMS"
            placeholder={"Select "}
            inputRef={refFrontColor}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.palette.text.primary,
              },
              "& .MuiInputBase-input": {
                color: theme.palette.text.primary,
              },
              "& .MuiInputBase-input::placeholder": {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },
              "& .MuiSvgIcon-root": {
                color: theme.palette.text.primary,
              },
            }}
          />
        )}
      />
    );
  }),
);
export const SelectFrontColor2 = React.memo(
  forwardRef((props, ref) => {
    const refFrontColor = useRef();
    const [frontColorOptions, setFrontColorOptions] = useState([]);
    const [frontColor, setFrontColor] = useState(props?.value);
    const [rangeId, setRangeId] = useState(props?.data?.range2Id);
    const prevRangeId = useRef(props?.data?.range2Id);
    const theme = useTheme();

    useEffect(() => {
      setFrontColor(props?.value);
      setRangeId(props?.data?.range2Id);
    }, [props?.value, props?.data]);

    const getFrontColoresByVendor = async () => {
      if (!rangeId) return;

      let actualRangeId = rangeId;

      if (/^[A-Z]+/.test(rangeId)) {
        const rangeInfo = await OAuthService.getList(
          `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.ranges}/search/findRangeByProductId?productId=${rangeId}`,
        );

        actualRangeId = rangeInfo.data.id;
      }

      const frontColorUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.ranges}/${actualRangeId}/variations`;

      const list = await OAuthService.getList(frontColorUrl);

      const frontColores =
        list.data?._embedded?.rangeVariations?.map((frontColor) => ({
          value: frontColor.productId,
          label: frontColor.productName,
        })) || [];

      setFrontColorOptions(frontColores);
    };

    const handleFrontColorChange = (e, data) => {
      setFrontColor(data.label);
      const rowData = props?.node;
      rowData?.setDataValue("color2Id", data.value);
    };
    useEffect(() => {
      if (rangeId) {
        getFrontColoresByVendor();
      }
    }, [rangeId]);

    useEffect(() => {
      if (prevRangeId.current !== rangeId) {
        if (prevRangeId.current !== undefined) {
          setFrontColor("");
          const rowData = props?.node;
          rowData?.setDataValue("color2Id", "");
        }
        prevRangeId.current = rangeId;
      }
    }, [rangeId]);
    useEffect(() => {
      const rowId = props?.node?.data?.id;
      const isInvalid = !!rangeId && !frontColor;
      props.onValidityChange?.(rowId, "color2", isInvalid);
    }, [rangeId, frontColor]);

    useEffect(() => {
      return () => {
        const rowId = props?.node?.data?.id;
        props.onValidityChange?.(rowId, "color2", false);
      };
    }, []);
    const handleFocus = () => {
      props.api?.getCellEditorInstances().map((editor) => {
        if (editor?.getRange2Id) {
          setRangeId(editor.getRange2Id());
        }
      });
    };

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return frontColor;
        },
        refreshForNewRange2(newRangeId) {
          if (newRangeId !== rangeId) {
            setFrontColor("");
            setRangeId(newRangeId);
            const rowData = props?.node;
            rowData?.setDataValue("color2Id", "");
          }
        },
      };
    });

    const fieldId = props.colDef.field + "Id";
    const selectedValue =
      frontColorOptions.find(
        (obj) =>
          obj.value === props.data?.[fieldId] ||
          obj.label?.trim() === frontColor?.trim() ||
          frontColor?.includes(obj.label?.trim()),
      ) ||
      (frontColor
        ? { label: frontColor, value: props.data?.[fieldId] || frontColor }
        : null);

    const displayOptions = frontColorOptions;

    return (
      <Autocomplete
        options={displayOptions}
        value={selectedValue}
        isOptionEqualToValue={(option, val) =>
          option.value === val?.value || option.label === val?.label
        }
        onChange={handleFrontColorChange}
        onFocus={handleFocus}
        disableClearable
        sx={{
          "& .MuiInputBase-input": {
            color: theme.palette.text.primary,
          },
        }}
        PopperComponent={StyledPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            className="customFieldOMS"
            placeholder={"Select "}
            inputRef={refFrontColor}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input::placeholder": {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },

              "& .MuiSvgIcon-root": {
                color: theme.palette.text.primary,
              },
            }}
          />
        )}
      />
    );
  }),
);

export const SelectFrontColor3 = React.memo(
  forwardRef((props, ref) => {
    const refFrontColor = useRef();
    const [frontColorOptions, setFrontColorOptions] = useState([]);
    const [frontColor, setFrontColor] = useState(props?.value);
    const [rangeId, setRangeId] = useState(props?.data?.range3Id);
    const theme = useTheme();
    const prevRangeId = useRef(props?.data?.range3Id);

    useEffect(() => {
      if (prevRangeId.current !== rangeId) {
        if (prevRangeId.current !== undefined) {
          setFrontColor("");
          const rowData = props?.node;
          rowData?.setDataValue("color3Id", "");
        }
        prevRangeId.current = rangeId;
      }
    }, [rangeId]);
    useEffect(() => {
      setFrontColor(props?.value);
      setRangeId(props?.data?.range3Id);
    }, [props?.value, props?.data]);

    const getFrontColoresByVendor = async () => {
      if (!rangeId) return;

      let actualRangeId = rangeId;

      if (/^[A-Z]+/.test(rangeId)) {
        const rangeInfo = await OAuthService.getList(
          `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.ranges}/search/findRangeByProductId?productId=${rangeId}`,
        );

        actualRangeId = rangeInfo.data.id;
      }

      const frontColorUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.ranges}/${actualRangeId}/variations`;

      const list = await OAuthService.getList(frontColorUrl);

      const frontColores =
        list.data?._embedded?.rangeVariations?.map((frontColor) => ({
          value: frontColor.productId,
          label: frontColor.productName,
        })) || [];

      setFrontColorOptions(frontColores);
    };

    const handleFrontColorChange = (e, data) => {
      setFrontColor(data.label);
      const rowData = props?.node;
      rowData?.setDataValue("color3Id", data.value);
    };
    useEffect(() => {
      if (rangeId) {
        getFrontColoresByVendor();
      }
    }, [rangeId]);
    useEffect(() => {
      const rowId = props?.node?.data?.id;
      const isInvalid = !!rangeId && !frontColor;
      props.onValidityChange?.(rowId, "color3", isInvalid);
    }, [rangeId, frontColor]);

    useEffect(() => {
      return () => {
        const rowId = props?.node?.data?.id;
        props.onValidityChange?.(rowId, "color3", false);
      };
    }, []);
    const handleFocus = () => {
      props.api?.getCellEditorInstances().map((editor) => {
        if (editor?.getRange3Id) {
          setRangeId(editor.getRange3Id());
        }
      });
    };

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return frontColor;
        },
        refreshForNewRange3(newRangeId) {
          if (newRangeId !== rangeId) {
            setFrontColor("");
            setRangeId(newRangeId);
            const rowData = props?.node;
            rowData?.setDataValue("color3Id", "");
          }
        },
      };
    });

    const fieldId = props.colDef.field + "Id";
    const selectedValue =
      frontColorOptions.find(
        (obj) =>
          obj.value === props.data?.[fieldId] ||
          obj.label?.trim() === frontColor?.trim() ||
          frontColor?.includes(obj.label?.trim()),
      ) ||
      (frontColor
        ? { label: frontColor, value: props.data?.[fieldId] || frontColor }
        : null);

    const displayOptions = frontColorOptions;

    return (
      <Autocomplete
        options={displayOptions}
        value={selectedValue}
        isOptionEqualToValue={(option, val) =>
          option.value === val?.value || option.label === val?.label
        }
        onChange={handleFrontColorChange}
        onFocus={handleFocus}
        disableClearable
        sx={{
          "& .MuiInputBase-input": {
            color: theme.palette.text.primary,
          },
        }}
        PopperComponent={StyledPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            className="customFieldOMS"
            placeholder={"Select "}
            inputRef={refFrontColor}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input::placeholder": {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },

              "& .MuiSvgIcon-root": {
                color: theme.palette.text.primary,
              },
            }}
          />
        )}
      />
    );
  }),
);

export const SelectCarcass = React.memo(
  forwardRef((props, ref) => {
    const refCarcass = useRef();
    const [carcassOptions, setCarcassOptions] = useState([]);
    const [vendorNumber, setVendorNumber] = useState(props.data?.vendorNum);
    const [carcass, setCarcass] = useState(props?.value);

    const slot = props.colDef.field.match(/\d/)?.[0];
    const rangeIdField = `range${slot}Id`;
    const [rangeId, setRangeId] = useState(props?.data?.[rangeIdField]);

    const theme = useTheme();

    useEffect(() => {
      setCarcass(props?.value);
      setVendorNumber(props?.data?.vendorNum);
      setRangeId(props?.data?.[rangeIdField]); // NEW
    }, [props?.value, props?.data]);

    const getCarcassesByVendor = async () => {
      if (vendorNumber) {
        const CarcassByVenderUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findAllCarcassByVendor}`;
        const list = await OAuthService.getList(
          `${CarcassByVenderUrl}?vendorId=${vendorNumber}`,
        );
        let carcasses = list.data?._embedded?.carcasses;
        carcasses = carcasses.map((carcass) => ({
          value: carcass.productId,
          label: carcass.productName,
        }));
        setCarcassOptions(carcasses);
      }
    };

    const handleCarcassChange = (e, data) => {
      setCarcass(data.label);
      const rowData = props?.node;
      if (props.colDef.field === "carcass1") {
        rowData?.setDataValue("carcass1Id", data.value);
      } else if (props.colDef.field === "carcass2") {
        rowData?.setDataValue("carcass2Id", data.value);
      } else if (props.colDef.field === "carcass3") {
        rowData?.setDataValue("carcass3Id", data.value);
      }
    };
    useEffect(() => {
      if (vendorNumber) {
        getCarcassesByVendor();
      }
    }, [vendorNumber]);

    const handleFocus = () => {
      props.api?.getCellEditorInstances().map((editor) => {
        if (editor?.getVendorNum) {
          setVendorNumber(editor.getVendorNum());
        }
        if (slot === "1" && editor?.getRangeId) {
          setRangeId(editor.getRangeId());
        } else if (slot === "2" && editor?.getRange2Id) {
          setRangeId(editor.getRange2Id());
        } else if (slot === "3" && editor?.getRange3Id) {
          setRangeId(editor.getRange3Id());
        }
      });
    };

    useEffect(() => {
      const rowId = props?.node?.data?.id;
      const isInvalid = !!rangeId && !carcass;
      props.onValidityChange?.(rowId, props.colDef.field, isInvalid);
    }, [rangeId, carcass]);

    useEffect(() => {
      return () => {
        const rowId = props?.node?.data?.id;
        props.onValidityChange?.(rowId, props.colDef.field, false);
      };
    }, []);

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return carcass;
        },
        [`updateCarcassRangeId${slot}`]: function (newRangeId) {
          setRangeId(newRangeId);
        },
      };
    });

    const fieldId = props.colDef.field + "Id";
    const selectedValue =
      carcassOptions.find(
        (obj) =>
          obj.value === props.data?.[fieldId] ||
          obj.label?.trim() === carcass?.trim() ||
          carcass?.includes(obj.label?.trim()),
      ) ||
      (carcass
        ? { label: carcass, value: props.data?.[fieldId] || carcass }
        : null);

    const displayOptions =
      selectedValue &&
      !carcassOptions.some((o) => o.label === selectedValue.label)
        ? [...carcassOptions, selectedValue]
        : carcassOptions;

    return (
      <Autocomplete
        options={displayOptions}
        value={selectedValue}
        isOptionEqualToValue={(option, val) =>
          option.value === val?.value || option.label === val?.label
        }
        onChange={handleCarcassChange}
        onFocus={handleFocus}
        disableClearable
        PopperComponent={StyledPopper}
        sx={{
          "& .MuiInputBase-input": {
            color: theme.palette.text.primary,
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            className="customFieldOMS"
            placeholder={"Select "}
            inputRef={refCarcass}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.palette.text.primary,
              },
              "& .MuiInputBase-input": {
                color: theme.palette.text.primary,
              },
              "& .MuiInputBase-input::placeholder": {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },
              "& .MuiSvgIcon-root": {
                color: theme.palette.text.primary,
              },
            }}
          />
        )}
      />
    );
  }),
);
export const SelectHandle = React.memo(
  forwardRef((props, ref) => {
    const refCarcass = useRef();
    const [handleOptions, setHandleOptions] = useState([]);
    const [vendorNumber, setVendorNumber] = useState(props.data?.vendorNum);
    const [handle, setHandle] = useState(props?.value);

    const slot = props.colDef.field.match(/\d/)?.[0];
    const rangeIdField = `range${slot}Id`;
    const [rangeId, setRangeId] = useState(props?.data?.[rangeIdField]);

    const theme = useTheme();

    useEffect(() => {
      setHandle(props?.value);
      setVendorNumber(props?.data?.vendorNum);
      setRangeId(props?.data?.[rangeIdField]); // NEW
    }, [props?.value, props?.data]);

    const getHandlesByVendor = async () => {
      if (vendorNumber) {
        const handleByVenderUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findAllHandlesByVendor}`;
        const list = await OAuthService.getList(
          `${handleByVenderUrl}?vendorId=${vendorNumber}`,
        );
        let handles = list.data?._embedded?.handles;
        handles = handles.map((handle) => ({
          value: handle.productId,
          label: handle.productName,
        }));
        setHandleOptions(handles);
      }
    };

    const handleHandleChange = (e, data) => {
      setHandle(data.label);
      const rowData = props?.node;
      if (props.colDef.field === "handle1") {
        rowData?.setDataValue("handle1Id", data.value);
      } else if (props.colDef.field === "handle2") {
        rowData?.setDataValue("handle2Id", data.value);
      } else if (props.colDef.field === "handle3") {
        rowData?.setDataValue("handle3Id", data.value);
      }
    };
    useEffect(() => {
      if (vendorNumber) {
        getHandlesByVendor();
      }
    }, [vendorNumber]);

    const handleFocus = () => {
      props.api?.getCellEditorInstances().map((editor) => {
        if (editor?.getVendorNum) {
          setVendorNumber(editor.getVendorNum());
        }
        // NEW
        if (slot === "1" && editor?.getRangeId) {
          setRangeId(editor.getRangeId());
        } else if (slot === "2" && editor?.getRange2Id) {
          setRangeId(editor.getRange2Id());
        } else if (slot === "3" && editor?.getRange3Id) {
          setRangeId(editor.getRange3Id());
        }
      });
    };

    useEffect(() => {
      const rowId = props?.node?.data?.id;
      const isInvalid = !!rangeId && !handle;
      props.onValidityChange?.(rowId, props.colDef.field, isInvalid);
    }, [rangeId, handle]);

    useEffect(() => {
      return () => {
        const rowId = props?.node?.data?.id;
        props.onValidityChange?.(rowId, props.colDef.field, false);
      };
    }, []);

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return handle;
        },
        [`updateHandleRangeId${slot}`]: function (newRangeId) {
          setRangeId(newRangeId);
        },
      };
    });

    const fieldId = props.colDef.field + "Id";
    const selectedValue =
      handleOptions.find(
        (obj) =>
          obj.value === props.data?.[fieldId] ||
          obj.label?.trim() === handle?.trim() ||
          handle?.includes(obj.label?.trim()),
      ) ||
      (handle
        ? { label: handle, value: props.data?.[fieldId] || handle }
        : null);

    const displayOptions =
      selectedValue &&
      !handleOptions.some((o) => o.label === selectedValue.label)
        ? [...handleOptions, selectedValue]
        : handleOptions;

    return (
      <Autocomplete
        options={displayOptions}
        value={selectedValue}
        isOptionEqualToValue={(option, val) =>
          option.value === val?.value || option.label === val?.label
        }
        onChange={handleHandleChange}
        onFocus={handleFocus}
        disableClearable
        sx={{
          "& .MuiInputBase-input": {
            color: theme.palette.text.primary,
          },
        }}
        PopperComponent={StyledPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            className="customFieldOMS"
            placeholder={"Select "}
            inputRef={refCarcass}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.palette.text.primary,
              },
              "& .MuiInputBase-input": {
                color: theme.palette.text.primary,
              },
              "& .MuiInputBase-input::placeholder": {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },
              "& .MuiSvgIcon-root": {
                color: theme.palette.text.primary,
              },
            }}
          />
        )}
      />
    );
  }),
);
export const SelectShippingType = React.memo(
  forwardRef((props, ref) => {
    const refShippingType = useRef();
    const [shippingTypeOptions, setShippingTypeOptions] = useState([]);
    const [shippingType, setShippingType] = useState(props?.value);
    const theme = useTheme();

    useEffect(() => {
      setShippingType(props?.value);
    }, [props?.value]);

    const getShippingTypesList = async () => {
      const url = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.shippingTypes}`;
      try {
        const list = await OAuthService.getList(url + `?size=1000`);
        let shippingTypesData = list.data?._embedded?.shippingTypes;
        shippingTypesData = shippingTypesData.map((type) => {
          return {
            label: type.code,
            value: type.id,
            freightText: type.shippingMethodName,
          };
        });
        setShippingTypeOptions(shippingTypesData);
      } catch (error) {
        console.log(error.message);
      }
    };
    useEffect(() => {
      getShippingTypesList();
    }, []);

    const handleShippingTypeChange = (e, data) => {
      setShippingType(data.label);

      if (props?.data) {
        props.data.freight = data.freightText;
      }
      if (props?.node) {
        props.node.setDataValue("freight", data.freightText);
      }
    };

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return shippingType;
        },
      };
    });

    const selectedValue =
      shippingTypeOptions.find(
        (obj) => obj.label?.trim() === shippingType?.trim(),
      ) ||
      (shippingType
        ? {
            label: shippingType,
            value: props.data?.freightId || shippingType,
            freightText: props.data?.freight || shippingType,
          }
        : null);

    const displayOptions =
      selectedValue &&
      !shippingTypeOptions.some((o) => o.label === selectedValue.label)
        ? [...shippingTypeOptions, selectedValue]
        : shippingTypeOptions;

    return (
      <Autocomplete
        options={displayOptions}
        value={selectedValue}
        isOptionEqualToValue={(option, val) =>
          option.value === val?.value || option.label === val?.label
        }
        onChange={handleShippingTypeChange}
        disableClearable
        sx={{
          "& .MuiInputBase-input": {
            color: theme.palette.text.primary,
          },
        }}
        PopperComponent={StyledPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            className="customFieldOMS"
            placeholder={"Select "}
            inputRef={refShippingType}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input::placeholder": {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },

              "& .MuiSvgIcon-root": {
                color: theme.palette.text.primary,
              },
            }}
          />
        )}
      />
    );
  }),
);

export const SelectDate = React.memo(
  forwardRef((props, ref) => {
    const refDatePicker = useRef();
    const [data, setDate] = useState(props.value);

    useEffect(() => {
      setDate(props.value);
    }, [props.value]);

    const handleDateChange = (newValue) => {
      setDate(
        newValue && moment(newValue).isValid()
          ? moment(newValue).format("YYYY-MM-DD")
          : null,
      );
    };

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return data && moment(data).isValid()
            ? moment(data).format("YYYY-MM-DD")
            : "";
        },
      };
    });
    return (
      <CustomDatePicker
        className="customFieldOMS"
        format={"dd-MM-yyyy"}
        name={props.colDef.field}
        value={data ? data : !props.value ? null : props.value}
        handleChange={handleDateChange}
        ref={refDatePicker}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#744e4e",
            color: "#fff",
          },
          "& .MuiInputBase-input": {
            color: "#fff",
            WebkitTextFillColor: "#fff",
          },
          "& .MuiSvgIcon-root": {
            color: "#fff",
          },
        }}
      />
    );
  }),
);

export const ActualLoadingDate = React.memo(
  forwardRef((props, ref) => {
    const getWeekUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.getWeek}`;

    const refDatePicker = useRef();
    const [actualLoadingDate, setActualLoadingDate] = useState(props.value);
    const [actualLoadingWeek, setActualLoadingWeek] = useState(
      props.data.actualLoadingWeek,
    );

    useEffect(() => {
      setActualLoadingDate(props.value);
      setActualLoadingWeek(props.data?.actualLoadingWeek);
    }, [props.value, props.data]);

    const handleLoadingDateChange = (newValue) => {
      setActualLoadingDate(
        newValue && moment(newValue).isValid()
          ? moment(newValue).format("YYYY-MM-DD")
          : null,
      );
    };

    useEffect(() => {
      if (props.column.colId === "actualLoadingDate") {
        const getlist = async () => {
          try {
            if (actualLoadingDate && moment(actualLoadingDate).isValid()) {
              const list = await OAuthService.getList(
                `${getWeekUrl}?date=${actualLoadingDate}`,
              );
              const week = list.data;
              setActualLoadingWeek(week);

              const rowData = props?.node;
              const currentWeek = props.data.actualLoadingWeek;

              if (week && String(week) !== String(currentWeek)) {
                rowData.setDataValue("actualLoadingWeek", week);
              }
            }
          } catch (error) {
            console.log("error", error);
          }
        };
        getlist();
      }
    }, [actualLoadingDate]);

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return actualLoadingDate && moment(actualLoadingDate).isValid()
            ? moment(actualLoadingDate).format("YYYY-MM-DD")
            : "";
        },
        getActualLoadingWeek() {
          return actualLoadingWeek;
        },
      };
    });

    const handleFocusValidUntil = () => {
      props.api?.getCellEditorInstances().map((editor) => {
        if (editor?.getActualLoadingDate) {
          setActualLoadingDate(editor.getActualLoadingDate());
        }
      });
    };

    return (
      <CustomDatePicker
        className="customFieldOMS"
        format={"dd-MM-yyyy"}
        name={"actualLoadingDate"}
        onOpen={handleFocusValidUntil}
        value={
          actualLoadingDate
            ? actualLoadingDate
            : !props.data.actualLoadingDate
            ? null
            : props.data.actualLoadingDate
        }
        handleChange={handleLoadingDateChange}
        ref={refDatePicker}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#744e4e",
            color: "#fff",
          },
          "& .MuiInputBase-input": {
            color: "#fff",
            WebkitTextFillColor: "#fff",
          },
          "& .MuiSvgIcon-root": {
            color: "#fff",
          },
        }}
      />
    );
  }),
);

export const PlannedLoadingDate = React.memo(
  forwardRef((props, ref) => {
    const getWeekUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.getWeek}`;

    const refDatePicker = useRef();
    const [plannedLoadingDate, setPlannedLoadingDate] = useState(props.value);
    const [plannedLoadingWeek, setPlannedLoadingWeek] = useState(
      props.data.plannedLoadingWeek,
    );

    useEffect(() => {
      setPlannedLoadingDate(props.value);
      setPlannedLoadingWeek(props.data?.plannedLoadingWeek);
    }, [props.value, props.data]);

    const handleLoadingDateChange = (newValue) => {
      setPlannedLoadingDate(
        newValue && moment(newValue).isValid()
          ? moment(newValue).format("YYYY-MM-DD")
          : null,
      );
    };

    useEffect(() => {
      if (props.column.colId === "plannedLoadingDate") {
        const getlist = async () => {
          try {
            if (plannedLoadingDate && moment(plannedLoadingDate).isValid()) {
              const list = await OAuthService.getList(
                `${getWeekUrl}?date=${plannedLoadingDate}`,
              );
              const week = list.data;
              setPlannedLoadingWeek(week);

              const rowData = props?.node;
              const currentWeek = props.data.plannedLoadingWeek;

              if (week && String(week) !== String(currentWeek)) {
                rowData.setDataValue("plannedLoadingWeek", week);
              }
            }
          } catch (error) {
            console.log("error", error);
          }
        };
        getlist();
      }
    }, [plannedLoadingDate]);

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return plannedLoadingDate && moment(plannedLoadingDate).isValid()
            ? moment(plannedLoadingDate).format("YYYY-MM-DD")
            : "";
        },
        getPlannedLoadingWeek() {
          return plannedLoadingWeek;
        },
      };
    });

    const handleFocusValidUntil = () => {
      props.api?.getCellEditorInstances().map((editor) => {
        if (editor?.getPlannedLoadingDate) {
          setPlannedLoadingDate(editor.getPlannedLoadingDate());
        }
      });
    };

    return (
      <CustomDatePicker
        className="customFieldOMS"
        format={"dd-MM-yyyy"}
        name={"plannedLoadingDate"}
        onOpen={handleFocusValidUntil}
        value={
          plannedLoadingDate
            ? plannedLoadingDate
            : !props.data.plannedLoadingDate
            ? null
            : props.data.plannedLoadingDate
        }
        handleChange={handleLoadingDateChange}
        ref={refDatePicker}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#744e4e",
            color: "#fff",
          },
          "& .MuiInputBase-input": {
            color: "#fff",
            WebkitTextFillColor: "#fff",
          },
          "& .MuiSvgIcon-root": {
            color: "#fff",
          },
        }}
      />
    );
  }),
);

export const ActualLoadingWeek = React.memo(
  forwardRef((props, ref) => {
    const getDateUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.getDate}`;
    const weekOption = Array.from({ length: 52 }, (_, index) => index + 1);

    const [actualLoadingWeek, setActualLoadingWeek] = useState(props?.value);
    const [actualLoadingDate, setActualLoadingDate] = useState(null);
    const theme = useTheme();

    useEffect(() => {
      setActualLoadingWeek(props?.value);
    }, [props?.value]);

    const handleWeekChange = (event, newValue) => {
      setActualLoadingWeek(newValue);
    };

    const refLoadingWeek = useRef();

    useEffect(() => {
      if (props.column.colId === "actualLoadingWeek") {
        const getlist = async () => {
          try {
            if (actualLoadingWeek) {
              const list = await OAuthService.getList(
                `${getDateUrl}?week=${actualLoadingWeek}`,
              );
              const data = moment(list.data).format("YYYY-MM-DD");
              setActualLoadingDate(data);

              const rowData = props?.node;
              const currentDate = props.data.actualLoadingDate
                ? moment(props.data.actualLoadingDate).format("YYYY-MM-DD")
                : null;

              if (data && data !== currentDate) {
                rowData.setDataValue("actualLoadingDate", data);
              }
            }
          } catch (error) {
            console.log("error", error);
          }
        };
        getlist();
      }
    }, [actualLoadingWeek]);

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return actualLoadingWeek;
        },
        getActualLoadingDate() {
          return actualLoadingDate;
        },
      };
    });

    const handleFocus = () => {
      props.api?.getCellEditorInstances().map((editor) => {
        if (editor?.getActualLoadingWeek) {
          setActualLoadingWeek(editor.getActualLoadingWeek());
        }
      });
    };

    return (
      <Autocomplete
        options={weekOption}
        getOptionLabel={(weekOption) => weekOption.toString()}
        value={actualLoadingWeek}
        onChange={handleWeekChange}
        onFocus={handleFocus}
        disableClearable
        sx={{
          "& .MuiInputBase-input": {
            color: theme.palette.text.primary,
          },
        }}
        PopperComponent={StyledPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            className="customFieldOMS"
            inputRef={refLoadingWeek}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input::placeholder": {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },

              "& .MuiSvgIcon-root": {
                color: theme.palette.text.primary,
              },
            }}
          />
        )}
      />
    );
  }),
);

export const PlannedLoadingWeek = React.memo(
  forwardRef((props, ref) => {
    const getDateUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.getDate}`;
    const weekOption = Array.from({ length: 52 }, (_, index) => index + 1);

    const [plannedLoadingWeek, setPlannedLoadingWeek] = useState(props.value);
    const [plannedLoadingDate, setPlannedLoadingDate] = useState(
      props.data.plannedLoadingDate,
    );
    const theme = useTheme();

    useEffect(() => {
      setPlannedLoadingWeek(props.value);
    }, [props.value]);

    const handleWeekChange = (event, newValue) => {
      setPlannedLoadingWeek(newValue);
    };

    const refLoadingWeek = useRef();

    useEffect(() => {
      if (props.column.colId === "plannedLoadingWeek") {
        const getlist = async () => {
          try {
            if (plannedLoadingWeek) {
              const list = await OAuthService.getList(
                `${getDateUrl}?week=${plannedLoadingWeek}`,
              );
              const data = moment(list.data).format("YYYY-MM-DD");
              setPlannedLoadingDate(data);

              const rowData = props?.node;
              const currentDate = props.data.plannedLoadingDate
                ? moment(props.data.plannedLoadingDate).format("YYYY-MM-DD")
                : null;

              if (data && data !== currentDate) {
                rowData.setDataValue("plannedLoadingDate", data);
              }
            }
          } catch (error) {
            console.log("error", error);
          }
        };
        getlist();
      }
    }, [plannedLoadingWeek]);

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return plannedLoadingWeek;
        },
        getPlannedLoadingDate() {
          return plannedLoadingDate;
        },
      };
    });

    const handleFocus = () => {
      props.api?.getCellEditorInstances().map((editor) => {
        if (editor?.getPlannedLoadingWeek) {
          setPlannedLoadingWeek(editor.getPlannedLoadingWeek());
        }
      });
    };

    return (
      <Autocomplete
        options={weekOption}
        getOptionLabel={(weekOption) => weekOption.toString()}
        value={plannedLoadingWeek}
        onChange={handleWeekChange}
        onFocus={handleFocus}
        disableClearable
        PopperComponent={StyledPopper}
        sx={{
          "& .MuiInputBase-input": {
            color: theme.palette.text.primary,
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            className="customFieldOMS"
            inputRef={refLoadingWeek}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input": {
                color: theme.palette.text.primary,
              },

              "& .MuiInputBase-input::placeholder": {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },

              "& .MuiSvgIcon-root": {
                color: theme.palette.text.primary,
              },
            }}
          />
        )}
      />
    );
  }),
);

export const SelectStatus = React.memo(
  forwardRef((props, ref) => {
    const initialValue = [
      { value: "WAITING", label: "WAITING" },
      { value: "PENDING", label: "PENDING" },
      { value: "APPROVED", label: "APPROVED" },
      { value: "REJECTED", label: "REJECTED" },
      { value: "DECLINED", label: "DECLINED" },
    ];
    const refStatus = useRef();
    const [statusOptions, setStatusOptions] = useState(initialValue);
    const [status, setStatus] = useState(props?.value);

    useEffect(() => {
      setStatus(props?.value);
    }, [props?.value]);

    const handleStatusChange = (e, data) => {
      setStatus(data.label);
    };

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return status;
        },
      };
    });

    const selectedValue =
      statusOptions.find((obj) => obj.label?.trim() === status?.trim()) ||
      (status ? { label: status, value: status } : null);

    const displayOptions =
      selectedValue &&
      !statusOptions.some((o) => o.label === selectedValue.label)
        ? [...statusOptions, selectedValue]
        : statusOptions;

    return (
      <Autocomplete
        options={displayOptions}
        value={selectedValue}
        isOptionEqualToValue={(option, val) =>
          option.value === val?.value || option.label === val?.label
        }
        onChange={handleStatusChange}
        disableClearable
        PopperComponent={StyledPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={"Select "}
            className="customFieldOMS"
            inputRef={refStatus}
          />
        )}
      />
    );
  }),
);
export const CustomTextEditor = React.memo(
  forwardRef((props, ref) => {
    const isNumeric = props.isNumeric === true;
    const refValue = useRef();
    const [value, setValue] = useState(props?.value ?? "");
    const theme = useTheme();

    useEffect(() => {
      setValue(props?.value);
    }, [props?.value]);

    const handleChange = (e) => {
      const newValue = e.target.value;

      if (isNumeric) {
      
        if (newValue !== "" && !/^-?\d*\.?\d*$/.test(newValue)) {
          return;
        }
      }

      setValue(newValue);
      if (props.api) {
        props.api.dispatchEvent({
          type: "cellValueChanged",
          node: props.node,
          data: { ...props.node.data, [props.colDef.field]: newValue },
          colDef: props.colDef,
          column: props.column,
          newValue: newValue,
          oldValue: props.value,
        });
      }
    };

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          if (isNumeric) {
            const trimmed = String(value).trim();
            if (trimmed === "" || isNaN(Number(trimmed))) {
              return props.value; 
            }
            return trimmed;
          }
          return refValue.current ? refValue.current.value : value;
        },
      };
    });

    return (
      <TextField
        className="customFieldOMS"
        required
        type="text"
        variant="outlined"
        name={props.colDef.field}
        value={value}
        onChange={handleChange}
        inputRef={refValue}
        sx={{
          "& .MuiInputBase-input": {
            color: theme.palette.text.primary,
          },
          "& .MuiOutlinedInput-root": {
            color: theme.palette.text.primary,
          },
        }}
      />
    );
  }),
);